import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../../assets/icons/sidebar/sidebar-payment.svg';
import { ReactComponent as SlashIcon } from '../../../assets/icons/slash.svg';
import { ReactComponent as StopIcon } from '../../../assets/icons/stop-circle.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { CommercialSessionInterface } from '../../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableSessionFiltersApplied,
  fetchAllSessions,
  fetchListSessionFilters,
  updateSessionFilters,
  setSessionSortData,
} from '../../../redux/slices/ChargingSessionSlice';
// import { setSelectedDevice } from '../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
// import { setSidePanelData } from '../../../redux/slices/TableSidePanelSlice';
import CustomActionBar from '../../../shared-components/custom-action-bar/CustomActionBar';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
// import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
// import DeviceViewSidePanel from '../../../shared-components/table-side-panel/DeviceViewSidePanel';
import EndConsumerSidePanel from '../../../shared-components/table-side-panel/EndConsumerSidePanel';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
// import StationViewSidePanel from '../../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import occpEvent from '../../../utils/api/ocpp-events';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element | null;
  rightCorner: string;
  notification: boolean;
}

/**
 * Commercial Session Table Component
 * @param {any} param0 - The param0
 * @param {any} param0.currentPage - The currentPage prop
 * @param {any} param0.setCurrentPage - The setCurrentPage prop
 */
function CommercialSessionTable({
  currentPage,
  setCurrentPage,
}: {
  currentPage: string;
  setCurrentPage: (value: string) => void;
}) {
  const { t, i18n } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  // @ts-ignore
  const {
    payments: sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    TotalFinising,
    totalInprocess,
    totalterminated,
    // @ts-ignore
  } = useAppSelector((state) => state.chargingSession);
  // const [isPolling, setIsPolling] = useState<boolean>(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [activeStatus, setActiveStatus] = useState<string>('');
  const isStatusClicked = React.useRef(false);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    content: null,
    header: true,
    rightCorner: '',
    notification: false,
  });
  const { portalType } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Function to handle radio button change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
  };

  /**
   * Function to open account details page
   * @param {any} sessionDetails - The sessionDetails
   * @param {any} newTab - The newTab
   */
  const handleOpenAccountDetails = (
    sessionDetails: CommercialSessionInterface,
    newTab: boolean,
  ) => {
    try {
      const { accountCustomId } = sessionDetails;
      if (!accountCustomId) {
        throw new Error('Account ID not found');
      }

      const sourcePath =
        location.state?.source ||
        new URLSearchParams(location.search).get('source') ||
        location.pathname + location.search;

      const url = `/ev-admin/accounts/partners/details/payout-management/${accountCustomId}`;

      if (newTab) {
        window.open(url, '_blank');
      } else {
        navigate(`/ev-admin/accounts/partners/details/payout-management/${accountCustomId}`, {
          state: { source: sourcePath },
        });
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
        }),
      );
    }
  };

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };
  /**
   * Fetch the session details in redux
   * @param {any} data - The data
   */
  const fetchSessions = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(
        fetchAllSessions({
          ...data,
          ...(activeStatus ? { status: activeStatus } : {}),
        }),
      );
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...queryParameters, page, limit, ...sortValue });
  };

  /**
   * Function to handle stop button click
   * @param {any} data session data or list of ids
   */
  const handleStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session: any) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh();
        setTimeout(() => {
          handleRefresh();
        }, 1500);
        setTimeout(() => {
          handleRefresh();
        }, 3500);
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to handle force stop button click
   * @param {any} data session data or list of ids
   */
  const handleForceStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter(
        (session: any) => data.includes(session.id) || data.includes(session._id),
      );
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId || item.chargingTransactionId,
        forceStop: 'true',
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsForceStopRequestSuccess')
            : t('transactionForceStopRequestSuccess'),
        );
        handleRefresh();
        setTimeout(() => {
          handleRefresh();
        }, 1500);
        setTimeout(() => {
          handleRefresh();
        }, 3500);
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

    
  const column = [
    {
      label: t('chargingStartedAt'),
      accessorKey: 'transactionStartsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging started at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDate(rowData?.transactionStartsAt as any)}</span>
      ),
    },
    {
      label: t('chargingEndedAt'),
      accessorKey: 'transactionEndsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging ended at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDate(rowData?.transactionEndsAt as any)}</span>
      ),
    },
    ...(portalType === PortalType.NETWORKS
      ? [
        {
          label: t('accountId'),
          accessorKey: 'accountCustomId' as keyof CommercialSessionInterface,
          queryParamsKey: 'facilityId',
          queryParamsValue: 'accountTenantId',
          sidePanelComponent: (
            <FacilityDetailsSidePanel
              actionActive
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              fetchIdFrom="queryParams"
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
          handleClickExpandView: handleOpenAccountDetails,
          disableNewTab: true,
        },
        {
          label: t('transactionId'),
          accessorKey: 'transactionId' as keyof CommercialSessionInterface,
        },

        {
          label: t('stationName'),
          accessorKey: 'chargingStation' as keyof CommercialSessionInterface,
          queryParamsKey: 'stationId',
          queryParamsValue: 'chargingStationId',
          sidePanelComponent: (
            <StationSidePanel
              fetchIdFrom="queryParams"
              initialIsEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
        },
        {
          label: t('chargerName'),
          accessorKey: 'chargingPointName' as keyof CommercialSessionInterface,
          queryParamsKey: 'deviceId',
          queryParamsValue: 'chargingPointSn',
          sidePanelComponent: (
            <DeviceSidePanel
              fetchIdFrom="queryParams"
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
        },
        {
          label: t('chargerSN'),
          accessorKey: 'chargingPointSn' as keyof CommercialSessionInterface,
          queryParamsKey: 'deviceId',
          queryParamsValue: 'chargingPointSn',
          sidePanelComponent: (
            <DeviceSidePanel
              fetchIdFrom="queryParams"
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
        },
      ]
      : [
        {
          label: t('facilityName'),
          accessorKey: 'facilityAccountName' as keyof CommercialSessionInterface,
          sortAllow: false,
          queryParamsKey: 'facilityId',
          queryParamsValue: 'facilityTagId',
          sidePanelComponent: (
            <FacilityDetailsSidePanel
              actionActive
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              fetchIdFrom="queryParams"
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
          handleClickExpandView: handleOpenAccountDetails,
          disableNewTab: true,
        },

        {
          label: t('stationName'),
          accessorKey: 'chargingStation' as keyof CommercialSessionInterface,
          queryParamsKey: 'stationId',
          queryParamsValue: 'chargingStationId',
          sidePanelComponent: (
            <StationSidePanel
              fetchIdFrom="queryParams"
              initialIsEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
        },
        {
          label: t('chargerName'),
          accessorKey: 'chargingPointName' as keyof CommercialSessionInterface,
          queryParamsKey: 'deviceId',
          queryParamsValue: 'chargingPointSn',
          sidePanelComponent: (
            <DeviceSidePanel
              fetchIdFrom="queryParams"
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
        },
        {
          label: t('chargerSN'),
          accessorKey: 'chargingPointSn' as keyof CommercialSessionInterface,
          queryParamsKey: 'deviceId',
          queryParamsValue: 'chargingPointSn',
          sidePanelComponent: (
            <DeviceSidePanel
              fetchIdFrom="queryParams"
              isEditMode={isEditMode}
              onEditModeChange={setIsEditMode}
              onSuccess={() => {
                setIsEditMode(false);
                handleRefresh();
              }}
            />
          ),
        },
      ]),

    ...(portalType === PortalType.NETWORKS
      ? [
        {
          label: t('endConsumerName'),
          accessorKey: 'endConsumer' as keyof CommercialSessionInterface,
          queryParamsKey: 'userCustomId',
          queryParamsValue: 'userCustomId',
          sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
          /**
           * isSidePanelDisabled
           * @param {any} row row data
           * @returns {any} boolean
           */
          isSidePanelDisabled: (row: any) => row.isGuestUser,
        },

        {
          label: t('endConsumerId'),
          accessorKey: 'userCustomId' as keyof CommercialSessionInterface,
          queryParamsKey: 'userCustomId',
          queryParamsValue: 'userCustomId',
          sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
          /**
           * isSidePanelDisabled
           * @param {any} row row data
           * @returns {any} boolean
           */
          isSidePanelDisabled: (row: any) => row.isGuestUser,
        },
      ]
      : [
        {
          label: t('userName'),
          accessorKey: 'userCustomId' as keyof CommercialSessionInterface,
          sortAllow: false,
        },
      ]),

    {
      label: t('status'),
      accessorKey: 'status' as keyof CommercialSessionInterface,
      /**
       * Custom data for the status column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        let statusClass = '';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
          }
        }
        return (
          <span className={`status-badge ${statusClass}`}>
            <span className="status-text capitalize">
              {rowData?.status ? t(rowData.status) : t('na')}
            </span>
          </span>
        );
      },
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof CommercialSessionInterface,
      /**
       * Custom data for the actions column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        if (rowData?.status === 'inProgress') {
          const isStopAllowed = permissionCheck('partnerSessionStop');
          const isForceStopAllowed =
            permissionCheck('partnerSessionTerminate') ||
            permissionCheck('partnerSessionForceStop');

          return (
            <div className="stop-buttons">
              <span
                role="presentation"
                title={!isStopAllowed ? t('notAllowed') : t('stop')}>
                <StopIcon
                  onClick={() => {
                    if (isStopAllowed) handleStop(rowData);
                  }}
                />
              </span>
              <span
                role="presentation"
                title={!isForceStopAllowed ? t('notAllowed') : t('forceStop')}>
                <SlashIcon
                  onClick={() => {
                    if (isForceStopAllowed) handleForceStop(rowData);
                  }}
                />
              </span>
            </div>
          );
        }
        return <div>{t('na')}</div>;
      },
    },
  ];

  /**
   * Table pagination function
   * @param {any} value page and limit values
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSessionSortData(sortData));
    fetchSessions({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Poll session data every 10 seconds
   */
  // useEffect(() => {
  //   // let timeout: NodeJS.Timeout;
  //   // if (isPolling) {
  //   //   timeout = setTimeout(() => {
  //   //     handleRefresh();
  //   //     setIsPolling(false);
  //   //   }, 10000);
  //   // }
  //   // return () => clearTimeout(timeout);
  // }, [isPolling]);

  useEffect(() => {
    if (isFilterUpdated || sessions.length === 0 || isStatusClicked.current) {
      isStatusClicked.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableSessionFiltersApplied());
    }
  }, [filters, activeStatus]);

  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('sessions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  /**
   * Check if status filter is currently active
   * @param {string} statusValue - The status value to check
   * @returns {boolean} True if active
   */
  const isStatusActive = (statusValue: string): boolean => {
    return activeStatus === statusValue;
  };

  /**
   * Handle status badge click to apply/toggle filter
   * @param {string} statusValue - The status value to filter by
   */
  const handleStatusClick = (statusValue: string) => {
    isStatusClicked.current = true;
    setActiveStatus((previous) => (previous === statusValue ? '' : statusValue));
  };

  return (
    <div className="payment-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="stats-bar">
        <div className="stat-item" key="total">
          <span className="stat-label">Total</span>
          <span className="stat-value">{totalResults ?? 0}</span>
        </div>
        <div className="stat-divider" />
        <div
          className={`stat-badge finished-badge ${isStatusActive('finished') ? 'active' : ''}`}
          key="finished"
          role="presentation"
          onClick={() => handleStatusClick('finished')}>
          <span className="badge-dot" />
          Finished: {TotalFinising ?? 0}
        </div>
        <div
          className={`stat-badge inProgress-badge ${isStatusActive('inprogress') ? 'active' : ''}`}
          key="inprogress"
          role="presentation"
          onClick={() => handleStatusClick('inprogress')}>
          <span className="badge-dot" />
          In Progress: {totalInprocess ?? 0}
        </div>
        <div
          className={`stat-badge terminated-badge ${isStatusActive('terminated') ? 'active' : ''}`}
          key="terminated"
          role="presentation"
          onClick={() => handleStatusClick('terminated')}>
          <span className="badge-dot" />
          Terminated: {totalterminated ?? 0}
        </div>
      </div>
      <div className="table-content">
        <CustomTable
          dataKey="CommercialSessions"
          version="v2"
          header={column}
          autoRefreshEnabled
          disableAutoRefreshToggle
          data={sessions}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateSessionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListSessionFilters}
          hidedColumnNames={[
            'sessionAmount',
            'platformAmount',
            'networkRevenue',
            'sharableRevenue',
          ]}
          rightTopContent={
            <div className="revenue-management-toggle-wrap">
              {portalType === PortalType.NETWORKS && (
                <>
                  {' '}
                  <CustomRadioButton
                    name=""
                    value="commercial"
                    label={t('commercialCharger')}
                    selected={currentPage === 'commercial'}
                    onChangeFunc={handleAvailabilityChange}
                  />
                  <CustomRadioButton
                    name=""
                    value="personal"
                    label={t('personalCharger')}
                    selected={currentPage === 'personal'}
                    onChangeFunc={handleAvailabilityChange}
                  />
                </>
              )}
            </div>
          }
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          checkbox
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          selectKey="id"
          isRowSelectable={(row) => row.status === 'inProgress'}
          height={320}
        />
        <CustomActionBar
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          actions={[
            {
              label: t('stop'),
              // icon: <StopIcon />,
              buttonType: 'danger-outline',
              /**
               * handle stop
               * @todo
               */
              onClick: () => handleStop(selectedRows),
            },
            {
              label: t('forceStop'),
              // icon: <SlashIcon />,
              buttonType: 'error',
              /**
               * handle force stop
               * @todo
               */
              onClick: () => handleForceStop(selectedRows),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default CommercialSessionTable;
